import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { buildDemoOrdersDailyBundle } from '../data/mockOrdersDaily';
import { fetchOrdersDailySeries } from '../services/ordersDailyTruthClient';
import type {
  OrdersDailyProjectionSource,
  OrdersDailySeriesDay,
  OrdersDailySeriesResponse,
} from '../types/ordersDailyTruth';
import { clampOrdersDailyRange } from '../utils/ordersDailyPresentation';
import { seriesDaysFromOrdersRows } from '../utils/ordersDailySeriesFromRows';

function isAbortError(err: unknown): boolean {
  return (
    (err instanceof DOMException && err.name === 'AbortError')
    || (err instanceof Error && err.name === 'AbortError')
  );
}

/**
 * Per-day slim summaries for wallboard trend / CSV lookback — read from the
 * stored per-day summaries; the server never computes them on this request.
 * Days not computed yet come back in `unavailableDays` (a gap, never zero);
 * when the server requested them it says when to ask again, and this hook does.
 */
export function useOrdersDailySeries(
  startDate: string,
  endDate: string,
  opts?: {
    enabled?: boolean;
    demo?: boolean;
  },
) {
  const enabled = opts?.enabled !== false;
  const demo = opts?.demo === true;
  const range = useMemo(
    () => clampOrdersDailyRange(startDate, endDate),
    [startDate, endDate],
  );

  const [days, setDays] = useState<OrdersDailySeriesDay[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const [lastLoadedAt, setLastLoadedAt] = useState<string | null>(null);
  const [projection, setProjection] = useState<OrdersDailyProjectionSource | null>(null);
  const [preparing, setPreparing] = useState(false);
  const [staleDays, setStaleDays] = useState<OrdersDailySeriesResponse['stale_days']>([]);
  const [unavailableDays, setUnavailableDays] = useState<OrdersDailySeriesResponse['unavailable_days']>([]);

  const abortRef = useRef<AbortController | null>(null);
  const retryRef = useRef<number | null>(null);

  const load = useCallback(async (loadOpts?: { background?: boolean }) => {
    if (!enabled) {
      setDays([]);
      setStaleDays([]);
      setUnavailableDays([]);
      setError(null);
      setLoading(false);
      setPreparing(false);
      return;
    }
    const background = loadOpts?.background === true;
    if (retryRef.current !== null) {
      window.clearTimeout(retryRef.current);
      retryRef.current = null;
    }
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    if (!background) setLoading(true);
    setError(null);

    try {
      if (demo) {
        const bundle = buildDemoOrdersDailyBundle(range.startDate, range.endDate);
        if (ac.signal.aborted) return;
        setDays(seriesDaysFromOrdersRows(bundle.records, range.startDate, range.endDate));
        setStaleDays([]);
        setUnavailableDays([]);
        setProjection(null);
        setPreparing(false);
        setLastLoadedAt(new Date().toISOString());
        setLoading(false);
        return;
      }

      const result = await fetchOrdersDailySeries(range.startDate, range.endDate, {
        signal: ac.signal,
      });
      if (ac.signal.aborted) return;
      if ('preparing' in result) {
        setPreparing(true);
        setLoading(false);
        retryRef.current = window.setTimeout(() => {
          void load({ background: true });
        }, result.retryAfterSeconds * 1000);
        return;
      }
      setPreparing(false);
      setDays(result.days);
      setStaleDays(result.stale_days);
      setUnavailableDays(result.unavailable_days);
      setProjection(result.projection ?? null);
      setLastLoadedAt(new Date().toISOString());
      setLoading(false);
      if (result.refresh_requested && result.retry_after_seconds) {
        // Missing / stale days are being computed — pick them up when they land.
        retryRef.current = window.setTimeout(() => {
          void load({ background: true });
        }, result.retry_after_seconds * 1000);
      }
    } catch (err) {
      if (isAbortError(err) || ac.signal.aborted) return;
      setError(err instanceof Error ? err.message : 'Failed to load orders daily series');
      setLoading(false);
      setPreparing(false);
    }
  }, [demo, enabled, range.endDate, range.startDate]);

  useEffect(() => {
    void load();
    return () => {
      abortRef.current?.abort();
      if (retryRef.current !== null) window.clearTimeout(retryRef.current);
    };
  }, [load]);

  const refresh = useCallback(
    (refreshOpts?: { background?: boolean }) => load(refreshOpts),
    [load],
  );

  return {
    days,
    staleDays,
    unavailableDays,
    loading,
    error,
    lastLoadedAt,
    projection,
    preparing,
    refresh,
    range,
  };
}
